import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { LanguageProvider } from "./contexts/LanguageContext";

// Error Boundary to prevent white/black screen crashes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("App Error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#07070A", color: "#F2F2F5", fontFamily: "sans-serif", textAlign: "center", padding: "20px" }}>
          <div>
            <h1 style={{ fontFamily: "serif", fontStyle: "italic", fontSize: "36px", marginBottom: "16px" }}>Monaco Bar</h1>
            <p style={{ color: "#999", marginBottom: "20px" }}>Ein Fehler ist aufgetreten. Bitte laden Sie die Seite neu.</p>
            <button onClick={() => window.location.reload()} style={{ background: "#FF1B3D", color: "#fff", border: "none", padding: "12px 24px", borderRadius: "8px", cursor: "pointer", fontSize: "16px" }}>
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Critical pages (loaded eagerly)
import HomePage from "./pages/HomePage";

// Lazy loaded pages
const GirlsPage = lazy(() => import("./pages/GirlsPage"));
const GirlDetailPage = lazy(() => import("./pages/GirlDetailPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const StudioBarPage = lazy(() => import("./pages/StudioBarPage"));
const BarPage = lazy(() => import("./pages/BarPage"));
const SepareePage = lazy(() => import("./pages/SepareePage"));
const AnfahrtsplanPage = lazy(() => import("./pages/AnfahrtsplanPage"));
const JobsPage = lazy(() => import("./pages/JobsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const NewsletterConfirmPage = lazy(() => import("./pages/NewsletterConfirmPage"));
const NewsletterPage = lazy(() => import("./pages/NewsletterPage"));
const GirlsRedirectPage = lazy(() => import("./pages/GirlsRedirectPage"));
const PolterabendPage = lazy(() => import("./pages/PolterabendPage"));

// Admin (always lazy)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminGirls = lazy(() => import("./pages/admin/AdminGirls"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminGallery = lazy(() => import("./pages/admin/AdminGallery"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminPolterabend = lazy(() => import("./pages/admin/AdminPolterabend"));

// Components
import AgeGate from "./components/AgeGate";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ScrollToTop from "./components/ScrollToTop";
import NewsletterPopup from "./components/NewsletterPopup";
import CookieBanner from "./components/CookieBanner";

// Context
export const AppContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Favorites Hook
export const useFavorites = () => {
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem("monaco_favorites");
    return saved ? JSON.parse(saved) : [];
  });

  const toggleFavorite = (girlId) => {
    setFavorites(prev => {
      const newFavs = prev.includes(girlId)
        ? prev.filter(id => id !== girlId)
        : [...prev, girlId];
      localStorage.setItem("monaco_favorites", JSON.stringify(newFavs));
      return newFavs;
    });
  };

  const isFavorite = (girlId) => favorites.includes(girlId);

  return { favorites, toggleFavorite, isFavorite };
};

// Auth Hook
export const useAuth = () => {
  const [token, setToken] = useState(() => localStorage.getItem("monaco_admin_token"));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);

  const login = (newToken) => {
    localStorage.setItem("monaco_admin_token", newToken);
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem("monaco_admin_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  return { token, isAuthenticated, login, logout };
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("monaco_admin_token");
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Main Layout
const MainLayout = ({ children, showAgeGate = true }) => {
  return (
    <div className="min-h-screen bg-monaco-bg-0">
      <Navbar />
      <main>{children}</main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

// Admin Layout
const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-monaco-bg-0">
      {children}
    </div>
  );
};

function App() {
  const [ageVerified, setAgeVerified] = useState(() => {
    return localStorage.getItem("monaco_age_verified") === "true";
  });
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);
  const [settings, setSettings] = useState(null);
  const favorites = useFavorites();
  const auth = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (e) {
        console.error("Failed to fetch settings:", e);
      }
    };
    fetchSettings();
  }, []);

  // Show popup on every visit after age verification
  useEffect(() => {
    if (ageVerified && !window.location.pathname.startsWith('/admin')) {
      setTimeout(() => {
        setShowNewsletterPopup(true);
      }, 1500);
    }
  }, [ageVerified]);

  const handleAgeVerify = () => {
    localStorage.setItem("monaco_age_verified", "true");
    setAgeVerified(true);
  };

  const handleCloseNewsletterPopup = () => {
    setShowNewsletterPopup(false);
  };

  // Check if current path is admin
  const isAdminRoute = window.location.pathname.startsWith('/admin');

  // Maintenance mode - show only for non-admin routes
  if (settings?.maintenance_mode && !isAdminRoute) {
    return (
      <div className="min-h-screen bg-monaco-bg-0 flex items-center justify-center px-4 relative overflow-hidden">
        {/* Red glow background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-monaco-red/10 rounded-full blur-[150px]" />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-monaco-red/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-monaco-red/5 rounded-full blur-[80px]" />
        </div>
        <div className="text-center relative z-10">
          <img src={settings?.logo_url || "/logo.png"} alt="Monaco Bar" className="h-20 mx-auto mb-10" />
          <h1 className="font-display text-3xl md:text-4xl text-monaco-text-0 mb-4 italic">Wartungsarbeiten</h1>
          <p className="text-monaco-text-1 text-lg">Wir sind in Kürze wieder zurück.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <LanguageProvider>
      <AppContext.Provider value={{ settings, favorites, auth, API }}>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" theme="dark" />
        
        {!ageVerified && !isAdminRoute && <AgeGate onVerify={handleAgeVerify} />}
        
        {/* Newsletter Popup - always show */}
        {showNewsletterPopup && (
          <NewsletterPopup onClose={handleCloseNewsletterPopup} />
        )}
        
        {/* Cookie Banner */}
        {ageVerified && !isAdminRoute && <CookieBanner />}
        
        <Suspense fallback={<div className="min-h-screen bg-monaco-bg-0" />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
          <Route path="/girls" element={
            <MainLayout>
              {settings?.girls_display_mode === "external" || (!settings?.girls_display_mode && settings?.girls_section_enabled === false) 
                ? <GirlsRedirectPage /> 
                : settings?.girls_display_mode === "visit"
                  ? <GirlsRedirectPage />
                  : <GirlsPage />
              }
            </MainLayout>
          } />
          <Route path="/girls/:slug" element={<MainLayout><GirlDetailPage /></MainLayout>} />
          <Route path="/buchen" element={<MainLayout><BookingPage /></MainLayout>} />
          <Route path="/gallery" element={<MainLayout><GalleryPage /></MainLayout>} />
          <Route path="/bar" element={<MainLayout><BarPage /></MainLayout>} />
          <Route path="/studio-bar" element={<MainLayout><BarPage /></MainLayout>} />
          <Route path="/separee" element={<MainLayout><SepareePage /></MainLayout>} />
          <Route path="/anfahrtsplan" element={<MainLayout><AnfahrtsplanPage /></MainLayout>} />
          <Route path="/jobs" element={<MainLayout><JobsPage /></MainLayout>} />
          <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
          <Route path="/newsletter" element={<MainLayout><NewsletterPage /></MainLayout>} />
          <Route path="/polterabend" element={<MainLayout><PolterabendPage /></MainLayout>} />
          <Route path="/impressum" element={<MainLayout><ImpressumPage /></MainLayout>} />
          <Route path="/datenschutz" element={<MainLayout><DatenschutzPage /></MainLayout>} />
          <Route path="/newsletter/confirm/:token" element={<MainLayout><NewsletterConfirmPage /></MainLayout>} />
          <Route path="/newsletter/unsubscribe/:token" element={<MainLayout><NewsletterConfirmPage type="unsubscribe" /></MainLayout>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLayout><AdminLogin /></AdminLayout>} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/girls" element={<ProtectedRoute><AdminLayout><AdminGirls /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/bookings" element={<ProtectedRoute><AdminLayout><AdminBookings /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/gallery" element={<ProtectedRoute><AdminLayout><AdminGallery /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/newsletter" element={<ProtectedRoute><AdminLayout><AdminNewsletter /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/contacts" element={<ProtectedRoute><AdminLayout><AdminContacts /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/audit-log" element={<ProtectedRoute><AdminLayout><AdminAuditLog /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/calendar" element={<ProtectedRoute><AdminLayout><AdminCalendar /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/statistics" element={<ProtectedRoute><AdminLayout><AdminStatistics /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/polterabend" element={<ProtectedRoute><AdminLayout><AdminPolterabend /></AdminLayout></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AppContext.Provider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
